import { Dialog } from "primereact/dialog";
import {
  AddDirectorAppointmentModalProps,
  Appointments,
  DropdownValue,
  MappedAppointments,
} from "../types/director";
import { Controller, useForm } from "react-hook-form";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import ReactCountryFlag from "react-country-flag";
import { getName } from "country-list";
import CustomeButton from "~/components/Button";
import {
  formatDateToISOStartOfDay,
  removeTimezoneFromDateString,
} from "~/utils/dateUtil";
import { useEffect, useRef, useState } from "react";
import { RadioButton } from "primereact/radiobutton";
import apiService from "~/services/service";
import API_ROUTES_V2 from "~/api-routes-v2";

const AddDirectorAppointmentModal = ({
  visible,
  onHideModal,
  setAppointments,
  editAppointmentEntry,
  isEditAppointment,
  appointments,
  isEditMode = false,
  addDirectorAppointmentHistory,
  directorId,
  entityId,
  updateDirectorAppointmentHistory,
  isLoading,
  setIsLoading,
  toastRef,
  addAsDirectorAndShareholder,
  otherEntitiesAppointments = [],
  currentEntityId,
  currentTenantId,
  isAddingForOtherEntity = false,
  onAddedAppointmentForOtherEntity,
}: AddDirectorAppointmentModalProps) => {
  const [duplicateError, setDuplicateError] = useState<string>("");
  const [allEntityOptions, setAllEntityOptions] = useState<
    { id: string; name: string; country: string; label: string }[]
  >([]);
  const [entitiesLoading, setEntitiesLoading] = useState(false);
  const prevStartDateRef = useRef<string | null>(null);
  const POSITIONS = [
    { name: "Executive Director", code: "Executive Director" },
    { name: "Non-Executive Director", code: "Non-Executive Director" },
    { name: "Managing Director", code: "Managing Director" },
  ];

  const ROLES = [
    { name: "Director", code: "Director" },
    { name: "Managing Director", code: "Managing Director" },
    { name: "Nominee Director", code: "Nominee Director" },
    { name: "Resident Director", code: "Resident Director" },
  ];

  const STATUS = [
    { name: "Active", code: "Active", styleClass: "active-circle-fill" },
    {
      name: "Not Active",
      code: "Not Active",
      styleClass: "not-active-circle-fill",
    },
    { name: "Removed", code: "Removed", styleClass: "remove-circle-fill" },
  ];
  const {
    control,
    formState: { errors, isValid, isDirty },
    handleSubmit,
    reset,
    watch,
    getValues,
  } = useForm<Appointments & { entity_id?: string }>({
    mode: "onChange",
    defaultValues: {
      position: null,
      role: null,
      title: "",
      status: null,
      start_date: null,
      end_date: null,
      entity_id: "",
    },
  });
  const position = watch("position");
  const start_date = watch("start_date");

  const HeaderTemplate = () => {
    return (
      <div className="flex flex-column gap-1">
        <p className="modal-title m-0">
          {isEditAppointment ? "Edit Appointment" : "Add New Appointment"}
        </p>
        <p className="modal-description m-0">
          {isEditAppointment
            ? "Update the appointment details below and hit “Save changes” when ready!"
            : "Fill in the appointment details below and hit when ready!"}
        </p>
      </div>
    );
  };

  const RoleOptionsTemplate = (
    option: DropdownValue,
    selectedValue: DropdownValue | null,
  ) => {
    return (
      <div className="flex align-items-center gap-2 role-options">
        <RadioButton
          checked={!!(selectedValue && selectedValue.code === option.code)}
        />
        <span>{option.name}</span>
      </div>
    );
  };

  const invokeAppointmentAPI = async (
    formattedData: any,
    appointmentId: string | undefined,
  ) => {
    try {
      setIsLoading?.(true);
      let response;
      if (isEditAppointment) {
        if (!appointmentId) throw new Error("Appointment Id not found");
        if (!directorId) throw new Error("Director Id not found");
        response = await updateDirectorAppointmentHistory?.(
          directorId,
          formattedData,
          appointmentId,
        );
      } else {
        if (!directorId) throw new Error("Director Id not found");
        response = await addDirectorAppointmentHistory?.(
          directorId,
          formattedData,
        );
      }
      return response;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setIsLoading?.(false);
    }
  };

  const addAppointmentHandler = async (data: any) => {
    try {
      const formattedData: any = {
        id: "",
        start_date: data.start_date,
        end_date: data.end_date,
        title: data.title,
        position: data.position?.code || "",
        role: data.role?.code || "",
        status: data.status?.code || "",
      };
      let entityIdValue: string | undefined;
      if (editAppointmentEntry && (editAppointmentEntry as any).entity_id) {
        const raw = (editAppointmentEntry as any).entity_id;
        entityIdValue =
          raw && typeof raw === "object" && "id" in raw
            ? (raw as { id: string }).id
            : raw;
      } else if (isAddingForOtherEntity) {
        const formEntityId = getValues("entity_id");
        entityIdValue =
          formEntityId &&
          typeof formEntityId === "object" &&
          "id" in formEntityId
            ? (formEntityId as { id: string }).id
            : (formEntityId as string | undefined);
      } else if (entityId) {
        entityIdValue = entityId;
      }
      if (entityIdValue) {
        formattedData.entity_id = entityIdValue;
      }
      if (isEditMode) {
        formattedData.start_date = data.start_date.split("T")[0];
        formattedData.end_date = data.end_date?.split("T")[0];
        const response = await invokeAppointmentAPI(
          formattedData,
          editAppointmentEntry?.id,
        );
        formattedData.id = response?.data?.id;
        if (
          isAddingForOtherEntity &&
          entityIdValue &&
          onAddedAppointmentForOtherEntity
        ) {
          const normalizedEntityId =
            typeof entityIdValue === "string"
              ? entityIdValue
              : entityIdValue &&
                  typeof entityIdValue === "object" &&
                  "id" in entityIdValue
                ? (entityIdValue as { id: string }).id
                : entityIdValue;
          if (normalizedEntityId) {
            await onAddedAppointmentForOtherEntity(normalizedEntityId);
          }
        }
      }
      setAppointments((prevAppointments: MappedAppointments[]) => {
        let updatedAppointments: MappedAppointments[];

        if (isEditAppointment && editAppointmentEntry) {
          // Compare by id if available (edit mode), otherwise by temp_id (onboarding mode)
          updatedAppointments = prevAppointments.map((appt) => {
            const matchById =
              editAppointmentEntry.id && appt.id === editAppointmentEntry.id;
            const matchByTempId =
              !editAppointmentEntry.id &&
              (appt as any).temp_id &&
              (appt as any).temp_id === (editAppointmentEntry as any).temp_id;

            if (matchById || matchByTempId) {
              return { ...formattedData, temp_id: (appt as any).temp_id };
            }
            return appt;
          });
        } else {
          // Adding new appointment - assign a temporary unique ID for onboarding mode
          const tempId = `temp_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
          updatedAppointments = [
            ...prevAppointments,
            { ...formattedData, temp_id: tempId },
          ];
        }

        // Sort by start_date in descending order
        return updatedAppointments.sort(
          (a, b) =>
            new Date(b.start_date || "").getTime() -
            new Date(a.start_date || "").getTime(),
        );
      });

      reset({
        position: null,
        role: null,
        title: "",
        status: null,
        start_date: null,
        end_date: null,
      });
      if (toastRef?.current) {
        toastRef.current?.show({
          severity: "success",
          summary: "Appointment History",
          detail: ` has been ${isEditAppointment ? "updated" : "added"} successfully.`,
          icon: "pi pi-check",
        });
      }
      onHideModal();
    } catch (error) {
      console.error(error);
    }
  };

  const cancelHandler = () => {
    reset({
      position: null,
      role: null,
      title: "",
      status: null,
      start_date: null,
      end_date: null,
      entity_id: "",
    });
    setDuplicateError("");
    onHideModal();
  };

  useEffect(() => {
    // in edit , keep the effect from running as it is initial render
    if (prevStartDateRef.current === null) {
      prevStartDateRef.current = start_date;
      return;
    }
    if (prevStartDateRef.current !== start_date) {
      reset(
        (prev) => ({
          ...prev,
          end_date: null,
        }),
        {
          keepErrors: true,
          keepDirty: true,
          keepTouched: true,
        },
      );
    }

    prevStartDateRef.current = start_date;
  }, [start_date, reset]);

  useEffect(() => {
    if (isEditAppointment && editAppointmentEntry) {
      const editEntityId = (editAppointmentEntry as any).entity_id;
      const entityIdStr =
        editEntityId && typeof editEntityId === "object" && "id" in editEntityId
          ? (editEntityId as { id: string }).id
          : editEntityId;
      reset({
        position: POSITIONS.find(
          (pos) => pos.code === editAppointmentEntry.position,
        ),
        role: ROLES.find((r) => r.code === editAppointmentEntry.role),
        status: STATUS.find((s) => s.code === editAppointmentEntry.status),
        title: editAppointmentEntry.title || "",
        start_date: editAppointmentEntry.start_date || null,
        end_date: editAppointmentEntry.end_date || null,
        entity_id: entityIdStr ?? "",
      });
    } else if (visible && !isEditAppointment) {
      reset({
        position: null,
        role: null,
        title: "",
        status: null,
        start_date: null,
        end_date: null,
        entity_id: "",
      });
    }
  }, [visible, isEditAppointment, editAppointmentEntry, reset]);

  useEffect(() => {
    if (
      visible &&
      isAddingForOtherEntity &&
      !isEditAppointment &&
      currentTenantId
    ) {
      const fetchTenantEntities = async () => {
        setEntitiesLoading(true);
        try {
          const response: any = await apiService(
            API_ROUTES_V2.FETCH_ENTITIES_BY_TENANT,
            "GET",
            {
              replace: {
                tenant_id: currentTenantId,
                page: "1",
                pageSize: "500",
                sortBy: "name",
                sortOrder: "asc",
              },
            },
          );
          const entities = response?.data?.entities || response?.data || [];
          const countryName = (code: string) => getName(code) || code;
          const options = entities
            .filter((e: any) => (e.id || e.original_pk) !== currentEntityId)
            .map((e: any) => ({
              id: e?.id || e?.original_pk,
              name: e?.name || "",
              country: e?.country || "",
              label: `${e?.name || ""} (${(e?.country && countryName(e?.country)) || e?.country || ""})`,
            }));
          setAllEntityOptions(options);
        } catch (err) {
          console.error("Error fetching tenant entities:", err);
          setAllEntityOptions([]);
        } finally {
          setEntitiesLoading(false);
        }
      };
      fetchTenantEntities();
    } else {
      setAllEntityOptions([]);
    }
  }, [
    visible,
    isAddingForOtherEntity,
    isEditAppointment,
    currentEntityId,
    currentTenantId,
  ]);

  useEffect(() => {
    if (position && start_date) {
      const positionCode = position?.code || position;

      // Determine if we're editing an Other Entity appointment
      // An appointment is from "Other Entities" if it has an entity_id that differs from currentEntityId
      // Use entityId as fallback if currentEntityId is not provided
      const currentEntity = currentEntityId || entityId;
      const isOtherEntityAppointment =
        isEditAppointment &&
        editAppointmentEntry?.entity_id &&
        currentEntity &&
        editAppointmentEntry.entity_id !== currentEntity;

      let isDuplicate = false;

      if (isOtherEntityAppointment) {
        // When editing an Other Entity appointment, validate against otherEntitiesAppointments
        // for the same entity_id
        const sameEntityAppointments = otherEntitiesAppointments.filter(
          (appt) => appt.entity_id === editAppointmentEntry?.entity_id,
        );
        isDuplicate = sameEntityAppointments.some((appt) => {
          const samePosition = appt.position === positionCode;
          const apptDateNormalized = appt.start_date?.split("T")[0];
          const startDateNormalized = start_date?.split("T")[0];
          const sameDate =
            apptDateNormalized === startDateNormalized ||
            appt.start_date === start_date;
          // Skip the current entry being edited (compare by ID)
          const isCurrentEntry = appt.id === editAppointmentEntry?.id;

          return samePosition && sameDate && !isCurrentEntry;
        });
      } else {
        // When editing/adding a This Entity appointment, validate against appointments
        isDuplicate = appointments.some((appt) => {
          const samePosition = appt.position === positionCode;
          const apptDateNormalized = appt.start_date?.split("T")[0];
          const startDateNormalized = start_date?.split("T")[0];
          const sameDate =
            apptDateNormalized === startDateNormalized ||
            appt.start_date === start_date;
          // Skip the current entry being edited (compare by ID for edit mode)
          const isCurrentEntry =
            isEditAppointment &&
            (appt.id === editAppointmentEntry?.id ||
              appt === editAppointmentEntry);

          return samePosition && sameDate && !isCurrentEntry;
        });
      }

      if (isDuplicate) {
        setDuplicateError("This position has been added before!");
      } else {
        setDuplicateError("");
      }
    } else {
      setDuplicateError("");
    }
  }, [
    position,
    start_date,
    isEditAppointment,
    editAppointmentEntry,
    otherEntitiesAppointments,
    currentEntityId,
    entityId,
    appointments,
  ]);

  const StatusValueTemplate = (value: {
    name: string;
    code: string;
    styleClass: string;
  }) => {
    if (!value)
      return (
        <div className="flex gap-1 align-items-center">
          <span className="p-dropdown-placeholder">{"Select status"}</span>
        </div>
      );
    return (
      <div className="flex gap-1 align-items-center">
        <>
          <i className={`pi pi-circle-fill ${value?.styleClass}`} />
          {value?.name}
        </>
      </div>
    );
  };

  const getMinEndDate = () => {
    if (!start_date) return undefined;
    const minDate = new Date(start_date);
    minDate.setDate(minDate.getDate() + 1);
    return minDate;
  };

  const isButtonDisabled = () => {
    if (!isValid || !!duplicateError || isLoading) {
      return true;
    }

    const entityIdVal = getValues("entity_id");
    const entityIdStr =
      entityIdVal && typeof entityIdVal === "object" && "id" in entityIdVal
        ? (entityIdVal as { id: string }).id
        : (entityIdVal as string | undefined);
    if (isAddingForOtherEntity && !isEditAppointment && !(entityIdStr ?? "")) {
      return true;
    }

    if (isEditAppointment) {
      return !isDirty;
    }

    return false;
  };
  const getSubmitButtonLabel = () => {
    if (addAsDirectorAndShareholder === "director") {
      return "Add as Director";
    } else if (addAsDirectorAndShareholder === "shareholder") {
      return "Add as Shareholder";
    }
  };
  return (
    <Dialog
      header={HeaderTemplate}
      visible={visible}
      style={{ width: "42vw" }}
      onHide={cancelHandler}
      className="add-appoinment-modal"
      draggable={false}
    >
      <div className="flex modal-container flex-column gap-3">
        <form onSubmit={handleSubmit(addAppointmentHandler)}>
          {isAddingForOtherEntity && !isEditAppointment && (
            <div className="flex flex-column col-12 pl-0 pr-0 gap-2">
              <label htmlFor="entity" className="flex align-items-center gap-1">
                Entity *
              </label>
              <Controller
                name="entity_id"
                control={control}
                rules={{ required: "Entity is required" }}
                render={({ field }) => {
                  const fieldValue =
                    field.value &&
                    typeof field.value === "object" &&
                    "id" in field.value
                      ? (field.value as { id: string }).id
                      : (field.value as string | undefined);

                  const selectedOption = allEntityOptions.find(
                    (opt) => opt.id === fieldValue,
                  );

                  return (
                    <>
                      <Dropdown
                        placeholder="Select entity"
                        className="w-full"
                        options={allEntityOptions}
                        optionLabel="label"
                        optionValue="id"
                        value={fieldValue ?? null}
                        onChange={(e) => {
                          let selectedId: string | null = null;
                          if (typeof e.value === "string") {
                            selectedId = e.value;
                          } else if (
                            e.value &&
                            typeof e.value === "object" &&
                            "id" in e.value
                          ) {
                            selectedId = (e.value as { id: string }).id;
                          }
                          field.onChange(selectedId || "");
                        }}
                        onBlur={field.onBlur}
                        loading={entitiesLoading}
                        filter
                        filterBy="name,label"
                        filterPlaceholder="Search entity"
                        itemTemplate={(option) => (
                          <div className="flex align-items-center gap-2">
                            {option.country && (
                              <ReactCountryFlag
                                countryCode={option.country}
                                svg
                                style={{ width: "1.25em", height: "1.25em" }}
                              />
                            )}
                            <span>{option.label}</span>
                          </div>
                        )}
                        valueTemplate={(option, props) => {
                          // Use the manually found option if PrimeReact doesn't provide it
                          const displayOption = option || selectedOption;
                          if (displayOption) {
                            return (
                              <div className="flex align-items-center gap-2">
                                {displayOption.country && (
                                  <ReactCountryFlag
                                    countryCode={displayOption.country}
                                    svg
                                    style={{
                                      width: "1.25em",
                                      height: "1.25em",
                                    }}
                                  />
                                )}
                                <span>{displayOption.label}</span>
                              </div>
                            );
                          }
                          return (
                            <span className="p-dropdown-placeholder">
                              {props.placeholder}
                            </span>
                          );
                        }}
                      />
                      {errors.entity_id && (
                        <small className="error-message">
                          {(errors.entity_id as { message?: string }).message ??
                            "Entity is required"}
                        </small>
                      )}
                    </>
                  );
                }}
              />
            </div>
          )}
          <div className="flex flex-column col-12 pl-0 pr-0  gap-2">
            <label htmlFor="position" className="flex align-items-center gap-1">
              Position *
            </label>
            <Controller
              name="position"
              control={control}
              rules={{
                required: "Director's position is required.",
              }}
              render={({ field }) => (
                <Dropdown
                  {...field}
                  placeholder="Select position"
                  optionLabel="name"
                  optionValue="code"
                  options={POSITIONS}
                  className={`w-full ${errors.position ? "error" : ""}`}
                />
              )}
            />
            {errors.position && (
              <small className="error-message">{errors.position.message}</small>
            )}
            {duplicateError && (
              <small className="error-message">{duplicateError}</small>
            )}
          </div>

          <div className="flex flex-column col-12 pl-0 pr-0  gap-2">
            <label htmlFor="position" className="flex align-items-center gap-1">
              Role *
            </label>
            <Controller
              name="role"
              control={control}
              rules={{
                required: "Director's role is required.",
              }}
              render={({ field }) => (
                <Dropdown
                  {...field}
                  placeholder="Select role"
                  optionLabel="name"
                  optionValue="code"
                  options={ROLES}
                  itemTemplate={(option) =>
                    RoleOptionsTemplate(option, field.value)
                  }
                  className={`w-full ${errors.role ? "error" : ""}`}
                />
              )}
            />
            {errors.role && (
              <small className="error-message">{errors.role.message}</small>
            )}
          </div>

          <div className="flex flex-column col-12 pl-0 pr-0  gap-2">
            <label htmlFor="title" className="flex align-items-center gap-1">
              Title
            </label>
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <InputText
                  {...field}
                  placeholder="Enter director's title"
                  type="text"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.title ? "error" : ""
                  }`}
                />
              )}
            />
            {errors.title && (
              <small className="error-message">{errors.title.message}</small>
            )}
          </div>

          <div className="flex flex-column col-12 pl-0 pr-0  gap-2">
            <label htmlFor="status" className="flex align-items-center gap-1">
              Status *
            </label>
            <Controller
              name="status"
              control={control}
              rules={{
                required: "Status is required.",
              }}
              render={({ field }) => (
                <Dropdown
                  {...field}
                  placeholder="Select status"
                  optionLabel="name"
                  optionValue="code"
                  dataKey="code"
                  options={STATUS}
                  itemTemplate={StatusValueTemplate}
                  valueTemplate={StatusValueTemplate}
                  className={`w-full ${errors.status ? "error" : ""}`}
                />
              )}
            />
            {errors.status && (
              <small className="error-message">{errors.status.message}</small>
            )}
          </div>

          <div className="flex flex-column col-12 pl-0 pr-0  gap-2">
            <label
              htmlFor="start_date"
              className="flex align-items-center gap-1"
            >
              Appointment Date *
            </label>
            <Controller
              name="start_date"
              control={control}
              rules={{
                required: "Appointment date is required.",
              }}
              render={({ field }) => (
                <Calendar
                  value={
                    field.value
                      ? removeTimezoneFromDateString(field.value)
                      : null
                  }
                  onChange={(e) => {
                    const newDate = formatDateToISOStartOfDay(e.value);
                    field.onChange(newDate);
                  }}
                  placeholder="Select start date"
                  className="w-full"
                />
              )}
            />
            {errors.start_date && (
              <small className="error-message">
                {errors.start_date.message}
              </small>
            )}
          </div>

          <div className="flex flex-column col-12 pl-0 pr-0  gap-2">
            <label htmlFor="end_date" className="flex align-items-center gap-1">
              End Date
            </label>
            <Controller
              name="end_date"
              control={control}
              render={({ field }) => (
                <Calendar
                  value={
                    field.value
                      ? removeTimezoneFromDateString(field.value)
                      : null
                  }
                  onChange={(e) => {
                    const newDate = formatDateToISOStartOfDay(e.value);
                    field.onChange(newDate);
                  }}
                  placeholder="Select end date"
                  className="w-full"
                  minDate={getMinEndDate()}
                  disabled={!start_date}
                />
              )}
            />
            {errors.end_date && (
              <small className="error-message">{errors.end_date.message}</small>
            )}
          </div>

          <div className="footer flex justify-content-end col-12 pl-0 pr-0 gap-2">
            <CustomeButton
              label={isEditAppointment ? "Discard" : "Cancel"}
              variant="outlined"
              className="cancel-button m-0"
              onClick={cancelHandler}
              type="button"
            />
            {addAsDirectorAndShareholder ? (
              <CustomeButton
                label={getSubmitButtonLabel()}
                variant="contained"
                className={`assign-button m-0 ${isButtonDisabled() && "disable"}`}
                disabled={isButtonDisabled()}
                loading={isEditMode && isLoading}
                type="button"
                onClick={handleSubmit(addAppointmentHandler)}
              />
            ) : (
              <CustomeButton
                label={isEditAppointment ? "Save Changes" : "Add Appointment"}
                variant="contained"
                className={`assign-button m-0 ${isButtonDisabled() && "disable"}`}
                disabled={isButtonDisabled()}
                loading={isEditMode && isLoading}
                type="submit"
              />
            )}
          </div>
        </form>
      </div>
    </Dialog>
  );
};

export default AddDirectorAppointmentModal;
